import { useMemo, useRef, useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import { getEloLevel } from '../../engine';
import { LevelChip, MobileSheet, initials, useSheetIds } from './mobileUi';
import { useT } from '../../i18n';

/**
 * Normalised name, for spotting two players who share one.
 *
 * Only used to decide whether a hint is needed — never to match identities.
 */
export function normName(name: unknown): string {
  return String(name ?? '')
    .trim()
    .toLowerCase();
}

/**
 * A recognisable hint, not an address: `ka••@kl•••.no`.
 *
 * This list is the FIRST thing the public member app shows — "Hvem er du?",
 * before any login, because the member app is public by design. It previously
 * printed every listed player's full e-mail address, so opening the app handed
 * a visitor 40 real people's contact details, and the name search revealed any
 * player's on demand.
 *
 * The address was there to separate two players with the same name, which is a
 * real problem: pick the wrong "Ole Hansen" and your bookings attach to a
 * stranger. So the hint stays for exactly that case and is masked — enough for
 * the right person to recognise their own, not enough to harvest.
 *
 * HISTORY: this was written when the underlying exposure was an openly
 * readable Supabase store (#190). That store is gone, and the real fix landed
 * server-side — `memberProjection.ts` masks the hint before it is sent and
 * strips addresses from every other member's row (#207), so the browser never
 * receives what this masks. This function is now defence in depth, not the
 * boundary.
 */
export function maskEmail(email: unknown): string {
  const raw = String(email ?? '').trim();
  const at = raw.lastIndexOf('@');
  if (at < 1) return '';
  const user = raw.slice(0, at);
  const domain = raw.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  const keep = (s: string, n: number) => s.slice(0, n) + '•'.repeat(Math.max(1, s.length - n));
  // A one- or two-character local part would be given away entirely by keeping
  // even one character, so mask it whole. Two people who share a name AND have
  // such short addresses become indistinguishable again — vanishingly rare, and
  // the wrong side to err on.
  const maskedUser = user.length <= 2 ? '•'.repeat(user.length) : keep(user, 2);
  const maskedDomain =
    dot > 0 ? `${keep(domain.slice(0, dot), 2)}${domain.slice(dot)}` : keep(domain, 2);
  return `${maskedUser}@${maskedDomain}`;
}

export interface MemberPickerProps {
  players: any[];
  currentId: any;
  title?: string;
  hint?: string;
  /** false on first launch: a member must be picked before the app is usable. */
  dismissible?: boolean;
  onPick: (id: any) => Promise<void> | void;
  onClose: () => void;
}

// Bottom sheet for choosing WHO is using the app. Until real accounts exist
// this is how a tester becomes a member: pick a player, and every «my» list,
// sign-up and booking in the app is done as that player.
export function MemberPicker({
  players,
  currentId,
  title: titleProp,
  hint: hintProp,
  dismissible = true,
  onPick,
  onClose,
}: MemberPickerProps) {
  // RESOLVED IN THE BODY, not as a default parameter: a default cannot call
  // `useT()`. The props still win when a caller supplies its own wording.
  const t = useT();
  const ids = useSheetIds();
  // Focus lands in the search box, as `autoFocus` used to put it.
  const search = useRef<HTMLInputElement>(null);
  const title = titleProp ?? t('mobile.memberPicker.title');
  const hint = hintProp ?? t('mobile.memberPicker.hint');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * Names that more than one player shares, so the picker can disambiguate
   * them — and only them.
   */
  const ambiguous = useMemo(() => {
    const seen = new Map<string, number>();
    for (const p of players || []) {
      const n = normName((p as { name?: string }).name);
      if (n) seen.set(n, (seen.get(n) ?? 0) + 1);
    }
    return new Set([...seen].filter(([, n]) => n > 1).map(([name]) => name));
  }, [players]);

  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (players || [])
      .filter(
        (p: any) =>
          !n ||
          String(p.name || '')
            .toLowerCase()
            .includes(n),
      )
      .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || ''), 'nb'))
      .slice(0, 40);
  }, [players, q]);

  return (
    <MobileSheet
      labelledBy={ids.title}
      describedBy={ids.lead}
      // Not when forced: a first launch must pick before the app is usable, so
      // neither Escape nor the scrim dismisses it.
      onClose={dismissible ? onClose : null}
      initialFocus={search}
      className="pb-4 flex flex-col max-h-[85%]"
    >
      <div className="flex items-start justify-between mt-3">
        <div>
          <h2
            id={ids.title}
            className="font-display font-semibold text-ink"
            style={{ fontSize: 20 }}
          >
            {title}
          </h2>
          <p id={ids.lead} className="text-[12px] text-ink-3">
            {hint}
          </p>
        </div>
        {dismissible && (
          <button
            onClick={onClose}
            aria-label={t('mobile.common.close')}
            className="p-1 text-ink-3"
          >
            <X size={18} />
          </button>
        )}
      </div>
      <div
        className="flex items-center gap-2 rounded-xl px-3 py-2 mt-3"
        style={{ background: '#eef0ea' }}
      >
        <Search size={15} className="text-ink-3" />
        <input
          ref={search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('mobile.memberPicker.search')}
          // A placeholder is not a label: it is gone once you type, and it
          // is not a name testing-library or every screen reader will use.
          aria-label={t('mobile.memberPicker.searchLabel')}
          className="flex-1 bg-transparent text-[14px] text-ink"
        />
      </div>
      <div className="mt-2 overflow-y-auto scrollbar-thin -mx-1 px-1 space-y-1">
        {list.map((p: any) => {
          const active = String(p.id) === String(currentId);
          const lvl = getEloLevel(Math.round(p.elo || 0));
          return (
            <button
              key={p.id}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onPick(p.id);
                } finally {
                  setBusy(false);
                }
              }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl text-left disabled:opacity-50"
              style={{
                background: active ? '#f7fdde' : '#fff',
                border: `1px solid ${active ? '#c2f02f' : '#ecefe8'}`,
              }}
            >
              <span
                className="flex items-center justify-center rounded-full font-display font-semibold flex-shrink-0"
                style={{
                  width: 36,
                  height: 36,
                  background: lvl.color,
                  color: lvl.text,
                  fontSize: 13,
                }}
              >
                {initials(p.name)}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-semibold text-ink truncate">{p.name}</span>
                {ambiguous.has(normName(p.name)) && p.email && (
                  <span className="block text-[11px] text-ink-3 truncate">
                    {maskEmail(p.email)}
                  </span>
                )}
              </span>
              <LevelChip elo={Math.round(p.elo || 0)} compact />
              {active && <Check size={16} className="text-lime-deep" strokeWidth={2.5} />}
            </button>
          );
        })}
        {list.length === 0 && (
          <div className="text-center text-[12px] text-ink-3 py-6">
            {t('mobile.common.noMatches')}
          </div>
        )}
      </div>
    </MobileSheet>
  );
}
